// The Farming unit editor's list: single-block fields as plain block rows,
// multi-block (and empty) fields as collapsible groups with an editable name.
// Rows are selectable (Shift/Cmd/Ctrl-click to multi-select) and draggable into
// or out of groups.
const React = require('react');
const { useState } = require('react');
const { useTranslation } = require('react-i18next');
const { isGroupField } = require('./layoutHelpers');

const trashIcon = require('images/icons/trash.svg');

const DRAG_TYPE = 'text/plain';

const readDraggedKey = (event) => event.dataTransfer.getData(DRAG_TYPE);

const allowDrop = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const BlockRow = ({
  block,
  isSelected,
  readOnly,
  onSelect,
  onRename,
  onDelete,
}) => (
  <div
    className={'ns-block-row' + (isSelected ? ' ns-row-selected' : '')}
    draggable={!readOnly}
    onDragStart={(event) => event.dataTransfer.setData(DRAG_TYPE, block.key)}
    onMouseDown={(event) => onSelect(block.key, event)}
  >
    <span className='ns-drag-handle' aria-hidden='true'>
      ⋮⋮
    </span>
    <input
      className='ns-row-input'
      value={block.name}
      readOnly={readOnly}
      onChange={(event) => onRename(block.key, event.target.value)}
    />
    {isSelected && !readOnly && (
      <button
        type='button'
        className='ns-icon-btn'
        onMouseDown={(event) => event.stopPropagation()}
        onClick={() => onDelete(block.key)}
      >
        <img src={trashIcon} alt='' />
      </button>
    )}
  </div>
);

const FieldGroup = ({ field, isSelected, readOnly, rowProps, handlers }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return (
    <div
      className={'ns-field-group' + (isSelected ? ' ns-row-selected' : '')}
      onDragOver={readOnly ? undefined : allowDrop}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handlers.onMoveBlock(readDraggedKey(event), field.key);
      }}
    >
      <div
        className='ns-field-header'
        onMouseDown={() => handlers.onSelectField(field.key)}
      >
        <button
          type='button'
          className='ns-chevron'
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        >
          {isCollapsed ? '›' : '⌄'}
        </button>
        <input
          className='ns-row-input ns-field-input'
          value={field.name}
          readOnly={readOnly}
          onChange={(event) =>
            handlers.onRenameField(field.key, event.target.value)
          }
        />
      </div>
      {!isCollapsed && (
        <div className='ns-field-blocks'>
          {field.blocks.map((block) => (
            <BlockRow key={block.key} block={block} {...rowProps(block)} />
          ))}
        </div>
      )}
    </div>
  );
};

const BlockList = ({
  draft,
  selectedKeys,
  selectedFieldKey,
  readOnly,
  handlers,
}) => {
  const { t } = useTranslation();
  const selected = new Set(selectedKeys);
  const rowProps = (block) => ({
    isSelected: selected.has(block.key),
    readOnly,
    onSelect: handlers.onSelectBlock,
    onRename: handlers.onRenameBlock,
    onDelete: handlers.onRequestDeleteBlock,
  });

  if (draft.fields.length === 0) {
    return <div className='ns-empty-text'>{t('new_season_empty_blocks')}</div>;
  }
  return (
    <div
      className='ns-block-list'
      onDragOver={readOnly ? undefined : allowDrop}
      onDrop={(event) => {
        event.preventDefault();
        handlers.onMoveBlock(readDraggedKey(event), null);
      }}
    >
      {draft.fields.map((field) =>
        isGroupField(field) ? (
          <FieldGroup
            key={field.key}
            field={field}
            isSelected={field.key === selectedFieldKey}
            readOnly={readOnly}
            rowProps={rowProps}
            handlers={handlers}
          />
        ) : (
          <BlockRow
            key={field.key}
            block={field.blocks[0]}
            {...rowProps(field.blocks[0])}
          />
        ),
      )}
    </div>
  );
};

module.exports = BlockList;
